/**
 * Frontend Error Logger
 * Sends errors to the backend for logging
 */

/**
 * Log an error to the backend
 */
export async function logError({ level = 'error', message, stack, additionalData = {} }) {
  try {
    await fetch(`${import.meta.env.VITE_API_URL}/errors/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        level,
        source: 'frontend',
        message,
        stack,
        url: window.location.href,
        additionalData
      })
    });
  } catch (err) {
    // Silently fail - don't want error logging to break the app
    console.error('Failed to log error to server:', err);
  }
}

/**
 * Set up global error handlers
 */
export function setupGlobalErrorHandlers() {
  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logError({
      level: 'error',
      message: `Unhandled Promise Rejection: ${event.reason?.message || event.reason}`,
      stack: event.reason?.stack,
      additionalData: {
        type: 'unhandledrejection',
        reason: String(event.reason)
      }
    });
  });

  // Catch global errors
  window.addEventListener('error', (event) => {
    logError({
      level: 'error',
      message: event.message || 'Unknown error',
      stack: event.error?.stack,
      additionalData: {
        type: 'error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    });
  });

  // Log console errors (optional - can be noisy)
  const originalConsoleError = console.error;
  console.error = function(...args) {
    // Call original
    originalConsoleError.apply(console, args);

    // Log to server (only first argument to avoid noise)
    if (args[0]) {
      logError({
        level: 'error',
        message: String(args[0]),
        stack: args[0]?.stack,
        additionalData: {
          type: 'console.error',
          args: args.slice(1, 3).map(String) // Include some context
        }
      });
    }
  };
}
